<script setup>
import { ref, computed, onMounted, watchEffect } from "vue"
import { useQuery, useMutation } from "@vue/apollo-composable"
import Modal from "@/components/common/Modal.vue"
import Banner from "@/components/common/Banner.vue"
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from "@headlessui/vue"
import CreatePaymentDetails from "@/pages/account/components/CreatePaymentDetails.vue"
import WISE_ACCOUNT_DETAILS from "@/graphql/PaymentOption/WiseGetAccountDetails.gql"

//QR AUTH LOGIC
//-----------------

import setupTwoFactorAuth from "@/graphql/Auth/Generate2FASetup.gql"
import verifyTwoFactorCode from "@/graphql/Auth/Verify2FACode.gql"

const qrCodeUrl = ref(null)
const validQR = ref(false)
const code = ref("")
const isValidQR = computed(() => validQR.value)
// ⚡ Use useMutation instead of useQuery
const { mutate: setupMutation } = useMutation(setupTwoFactorAuth)
const { mutate: verifyMutation } = useMutation(verifyTwoFactorCode)

const loadQRCode = async () => {
  try {
    const { data } = await setupMutation() // Call the mutation manually
    if (data?.generate2FASetup?.qrCodeUrl) {
      qrCodeUrl.value = data?.generate2FASetup?.qrCodeUrl
    }
  } catch (error) {
    console.error("Failed to setup 2FA", error)
  }
}

const verifyCode = async () => {
  try {
    const { data } = await verifyMutation({
      code: code.value,
    })
    if (data.verify2FACode == true) {
      alert("✅ Authenticated successfully!")
      validQR.value = true
    } else {
      alert("❌ Invalid code, try again.")
      validQR.value = false
    }
  } catch (err) {
    console.error("Failed to verify 2FA code", err)
  }
}

onMounted(loadQRCode)

//^^^^^^^^^
//QR AUTH LOGIC END

const props = defineProps({
  user: {
    type: Object,
    default: () => ({}),
  },
})

const wiseAccountDetails = ref(null)
const filters = ref({
  aDetails: {},
  name: {},
})
const email = ref(null)
const payOption = ref(null)
const currency = ref(null)
const resetDetails = ref(false)
const paymentEmail = computed(() => {
  // First try to get from billingContact, then from displayFields
  if (props.user?.billingContact?.email) {
    return props.user.billingContact.email
  }
  return email.value
})

const userId = computed(() => props.user?.id ?? undefined)
const accountId = computed(
  () => props.user?.userDetails?.wiseAccountId ?? undefined,
)

// Computed property for payment type options based on userPaymentServiceOptions or userId
const paymentTypeOptions = computed(() => {
  if (userId.value == 28430) {
    return ['Bank Transfer (via Wise)']
  }
  const userPaymentServiceOptions = props.user?.userDetails?.userPaymentServiceOptions
  if (userPaymentServiceOptions !== null && userPaymentServiceOptions !== undefined) {
    try {
      return JSON.parse(userPaymentServiceOptions)
    } catch {
      // Invalid JSON, fall through to default options
    }
  }
})

// Create Filters Function
const createFilters = data => {
  const excludedKeys = ["balanceAccountProfileId", "hashedByLooseHashAlgorithm", "isSmartRouting"]

  for (const key in data.recipientAccount) {
    let value = data.recipientAccount[key]
    if (key === "displayFields") {
      for (const vKey in value) {
        if (value[vKey]["key"] === "email") {
          email.value = value[vKey]["value"]
        }
      }
      continue
    }

    if (key === "__typename" || key === "displayFields") continue

    if (key === "type") {
      payOption.value = value
      continue
    }

    if (key === "currency" && resetDetails.value) continue
    if (key === "currency" && !resetDetails.value) {
      currency.value = value
      continue
    }

    if (typeof value === "object" && value !== null) {
      const innerObject = {}
      for (const vKey in value) {
        if (vKey === "__typename" || excludedKeys.includes(vKey)) continue
        if (key === "address") {
          innerObject[`address.${vKey}`] = value[vKey]
        } else {
          innerObject[vKey] = value[vKey]
        }
      }

      // accountType and legalType to hold only the 'name' value
      if (key === "details") {
        if (innerObject.accountType && innerObject.accountType.name) {
          innerObject.accountType = innerObject.accountType.name
        }
        if (innerObject.legalType && innerObject.legalType.name) {
          innerObject.legalType = innerObject.legalType.name
        }

        filters.value.aDetails = innerObject
      } else {
        filters.value[key] = innerObject
      }
    } else {
      filters.value[key] = value
    }
  }

  // Merge address and aDetails if address exists in filters
  if (filters.value?.address !== undefined) {
    const merged = { ...filters.value.address, ...filters.value.aDetails }
    filters.value.aDetails = merged
  }

  if (resetDetails.value) {
    payOption.value = null
    filters.value.aDetails = {}
    resetDetails.value = false
  }
}
const formatLabels = name => {
  if (name === "abartn") return "Routing Number"
  if (name === "address.firstLine") return "Recipient Address"
  if (name === "address.country") return "Recipient Country"
  if (name === "address.postCode") return "Recipient Post Code"
  if (name === "address.city") return "Recipient City"
  if (name === "address.state") return "Recipient Region"

  return name
    .split(".")
    .map(part =>
      part
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, str => str.toUpperCase())
        .trim(),
    )
    .join(" ")
}

// Format Fields
const formattedTitle = computed(() => {
  if (!payOption.value?.title) return ""

  const titleMap = {
    ach: "ACH",
    fedWireLocal: "Wire",
    iban: "IBAN",
    aba: "ACH",
  }

  return (
    titleMap[payOption.value.title] ||
    payOption.value.title
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/(^|\s)([a-z])/g, (match, p1, p2) => p1 + p2.toUpperCase())
  )
})

// Wise Account Details Available
const shouldFetchAccountDetails = computed(() => !!props.user?.userDetails)
const { loading: isWiseLoading, onResult: getWiseAccountDetails } = useQuery(
  WISE_ACCOUNT_DETAILS,
  () => ({
    input: { wiseAccountId: accountId.value, userId: userId.value },
  }),
  {
    enabled: shouldFetchAccountDetails,
  },
)
getWiseAccountDetails(queryResult => {
  if (queryResult.data !== undefined) {
    wiseAccountDetails.value = queryResult.data
    createFilters(queryResult.data.wiseGetAccountDetails)
  }
})

function capitalize(word) {
  if (!word) return ""
  return word.charAt(0).toUpperCase() + word.slice(1)
}

// Only show a payment method's saved details if that method is still
// enabled for this user (paymentTypeOptions) AND the user actually has
// data for it. Falls back Bitcoin -> Wise -> nothing (default "Add" state).
const showBitcoinDetails = computed(
  () =>
    (paymentTypeOptions.value ?? []).includes("Crypto Wallet (BTC)") &&
    !!props.user?.userDetails?.cryptoWallet?.walletId,
)
const showWiseDetails = computed(
  () =>
    !showBitcoinDetails.value &&
    (paymentTypeOptions.value ?? []).includes("Bank Transfer (via Wise)") &&
    !!wiseAccountDetails.value?.wiseGetAccountDetails?.recipientAccount,
)
const hasVisiblePaymentDetails = computed(
  () => showBitcoinDetails.value || showWiseDetails.value,
)
</script>

<template>
  <div class="text-sm">
  <div class="flex flex-col">
    <div
      v-if="isWiseLoading"
      class="w-full flex justify-center text-base-content/25"
    >
      <v-icon
        class="ov-spin"
        name="ri-loader-5-line"
        height="1em"
        width="1em"
        scale="1.5"
      />
    </div>
    <div
      v-if="hasVisiblePaymentDetails && !isWiseLoading"
      class="grid grid-cols-1 md:grid-cols-2 gap-4"
    >
      <XInput
        v-if="showBitcoinDetails"
        :model-value="capitalize(user.userDetails.cryptoWallet?.walletType) || 'Bitcoin'"
        label="Cryptocurrency"
        readonly
      />

      <XInput
        v-if="showBitcoinDetails"
        :model-value="user.userDetails.cryptoWallet?.walletId"
        label="Wallet Address"
        readonly
      />
      <XInput
        v-if="showWiseDetails"
        :model-value="currency?.code || ''"
        label="Currency"
        readonly
      />
      <XInput
        v-if="showWiseDetails"
        :model-value="formattedTitle || ''"
        label="Pay Option"
        readonly
      />
      <XInput
        v-if="showWiseDetails"
        :model-value="filters.name?.fullName || ''"
        label="Account Holder"
        readonly
      />

      <XInput
        v-for="(value, key, index) in filters.aDetails"
        v-if="showWiseDetails"
        :key="index"
        :model-value="value || ''"
        :label="formatLabels(key)"
        readonly
      />
      <XInput
        v-if="hasVisiblePaymentDetails"
        :model-value="paymentEmail || ''"
        label="Payment Notifications Email"
        readonly
      />
    </div>
  </div>

  <!-- only show if we have not setup an authenticator -->

  <div
    v-if="qrCodeUrl && props.user?.is2faEnabled == false && validQR == false"
    class="flex flex-col gap-4 mt-4"
  >
  <div class="flex  flex-col md:flex-row gap-8 items-center">
    <div class="flex flex-col gap-4">
      <span class="font-semibold">Two Factor Authentication is required to edit payment details</span>
      <p>
        Scan the QR code with your preferred
        authenticator app (Google Authenticator recommended) to set up 2FA.
      </p>
      <Banner variant="warning" :simple="true">
        <div>
          <div class="flex items-center">
            <span class="text-xl pr-2">⚠️</span> <span class="font-semibold">2FA can’t be changed or transferred once set.</span> 
          </div>
          <div class="text-sm pt-2">
            Make sure you’re using the correct device and account for authentication. If multiple people access this account, they must share the same authenticator — or you risk permanent lockout.

          </div>

        </div>
      </Banner>
    </div>
    <div class="w-full">
      <div class="md:w-48 w-full bg-base-200 rounded-lg p-4 mx-auto border-base-content/20 border">
        <div class="bg-white rounded-lg flex justify-center items-center">
          <img :src="qrCodeUrl" alt="QR Code for 2FA" class="rounded-lg w-full mx-auto max-h-48 max-w-48"/>
        </div>
      <div class="">
        <XInput 
        v-model="code" 
        label="Enter 6-digit code" 
        class="w-full my-2"
        bgColor="base-200"
      ></XInput>
      <XButton color="primary" class="w-full" @click="verifyCode">
        Verify Code
      </XButton>
      </div>
    </div>
    </div>
    </div>


  </div>

  <!-- only show if we have setup an authenticator -->
  <div
    v-if="
      !validQR &&
      props.user?.is2faEnabled == true &&
      isValidQR == false &&
      props.user?.is2faValid != true
    "
    class="flex flex-col gap-4 mt-4"
  >
    <h2>
      To edit payment details please authenticate using 2FA authenticator code.
    </h2>
    <XInput v-model="code" label="Enter 6-digit code" class="!w-64"></XInput>
    <XButton color="primary" class="w-40" @click="verifyCode">
      Verify Code
    </XButton>
  </div>

  <!--HIde the option to update pay details untill qr is valid-->
  <div
    v-if="
      (!isWiseLoading && wiseAccountDetails && isValidQR) ||
      props.user?.is2faValid == true
    "
    class="flex flex-col gap-4 w-72"
  >
    <Modal :clickToClose="false">
      <template #default="{ toggle }">
        <XButton
          color="accent"
          @click="toggle"
          :class="[wiseAccountDetails ? 'mt-4' : '']"
          >{{ hasVisiblePaymentDetails ? "Change" : "Add" }}
          Payment Details</XButton>
      </template>
      <template #modal="{ close }">
        <CreatePaymentDetails
          class="w-full"
          :user="user"
          :changeDetails="wiseAccountDetails ? true : false"
          @close="close"
        />
      </template>
    </Modal>
  </div>
</div>
</template>

<style scoped>
</style>
